import React from "react";
import {
  Activity,
  BookOpen,
  ClipboardList,
  Clock,
  Compass,
  FileImage,
  FileSearch,
  LayoutDashboard,
  MessageSquare,
  Server,
  Share2,
  Shield,
  UserCog,
  Bot,
  Building2,
  Calendar,
  CreditCard,
  FlaskConical,
  Inbox,
  Pill,
  Stethoscope,
  Users,
} from "lucide-react";
import { Permission, UserRole } from "@/types/user";
import { ModuleKey } from "@/types/featureToggle";
import { shapeAtLeast, type ClinicShape } from "@/config/clinicShape";

/**
 * The sidebar's shape, as data.
 *
 * This lived inline in `MainLayout` — 330 lines of config in the middle of a
 * 900-line component, which meant every nav change was surgery on the layout
 * and no change could be reviewed on its own. Here it is a pure function of a
 * small context, so a nav edit is a diff to `buildNav` and nothing else.
 *
 * Two halves, deliberately separate:
 *   `buildNav`       what rows COULD exist — labels, icons, paths, gates
 *   `isItemVisible`  what this user may see — permissions, modules, roles
 *
 * `Sidebar` renders whatever survives the second. Keeping the predicate out
 * here (rather than inline in the component, where it used to live) is what
 * makes the nav answerable without mounting React — and it is the seam the
 * clinic-shape filter plugs into later.
 */

export interface SidebarItem {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  path?: string;
  children?: SidebarItem[];
  permission?: Permission | Permission[];
  roles?: UserRole[];
  moduleKey?: ModuleKey;
  /**
   * Optional counter pill on the right of the item. Hidden when 0/undefined
   * and when the sidebar is collapsed. Used for things like the
   * pending-refill-requests count surfaced on the Pharmacy entry.
   */
  badge?: number;
  /**
   * Smallest clinic this row is useful in. A solo doctor has no shared inbox
   * to sit in and no team to manage, so those rows are absent rather than
   * empty. Shape can only ever HIDE — the permission gates above still decide
   * what anyone may reach, and everything hidden stays in ⌘K and /explore.
   */
  minShape?: ClinicShape;
}

/** Everything `buildNav` needs from the app. Nothing React-shaped. */
export interface NavContext {
  isRTL: boolean;
  t: (key: string) => string;
  /** Pending refill requests, rendered as the Pharmacy badge. */
  pendingRefills: number;
  /**
   * NAV_DIET_ENABLED. False serves the pre-2026-08 sidebar exactly — 28
   * elements, five collapsible groups, Administration and Library intact —
   * so a clinic can be flipped between the two and back without a deploy.
   * Defaults to the old nav: a tenant opts IN to the new one.
   */
  navDiet?: boolean;
}

export function buildNav(ctx: NavContext): SidebarItem[] {
  return ctx.navDiet ? buildDietNav(ctx) : buildLegacyNav(ctx);
}

/**
 * The sidebar as it stood before the nav diet: 28 elements for an owner,
 * Administration and Library as groups, labs/imaging/referrals as three rows.
 *
 * Kept whole rather than deleted so NAV_DIET_ENABLED is a real switch and not
 * a one-way door — a clinic mid-pilot can be put back on the old shape in the
 * time it takes to toggle a flag, and the two can be compared side by side by
 * two staff in the same clinic.
 *
 * The permission FIXES are carried across on purpose (Referrals on
 * referral:read, Pharmacy on medical_records:read, Subscription on
 * system:read, the dead audit:read dropped). Those were bugs, not part of the
 * redesign; serving them broken again under a flag would just be a second bug.
 *
 * One deliberate difference: sections no longer collapse. `expandedSections`
 * defaulted to all-open, so nothing is hidden that used to be visible — the
 * chevrons are simply gone.
 */
function buildLegacyNav(ctx: NavContext): SidebarItem[] {
  const { isRTL, t, pendingRefills } = ctx;

  return [
      {
        id: "home",
        label: isRTL ? "الرئيسية" : "Home",
        icon: Activity,
        path: "/home",
      },
      {
        // Orientation. Permanent, not a one-shot tour: a 10+ entry product
        // needs a map you can come back to, and this is also where the
        // guided next-step lives.
        id: "explore",
        label: isRTL ? "استكشف" : "Explore",
        icon: Compass,
        path: "/explore",
      },
      {
        // Chat-with-MAIE is a primary surface now (orders, tasks, patient
        // Q&A, research) — surfaced in the nav so it's discoverable, not
        // hidden behind the floating bubble's fullscreen button.
        id: "assistant",
        label: isRTL ? "اسأل MAIE" : "Ask MAIE",
        icon: Bot,
        path: "/assistant",
      },
      {
        // Dashboard is the administrative overview surface (clinic-scale
        // counts, billing, templates). Doctors land on `/home` by default;
        // they can still navigate here. Nurses/Receptionists don't hold
        // report:read, so the backend would 403 — hide the link entirely
        // rather than show a dead-end.
        id: "dashboard",
        label: t("nav.dashboard"),
        icon: LayoutDashboard,
        path: "/dashboard",
        permission: Permission.REPORT_READ,
      },
      {
        id: "patients",
        label: t("nav.patients"),
        icon: Users,
        path: "/patients",
        moduleKey: ModuleKey.PATIENT_MANAGEMENT,
      },
      {
        id: "schedule",
        label: t("nav.scheduling"),
        icon: Calendar,
        moduleKey: ModuleKey.APPOINTMENTS,
        children: [
          {
            id: "schedule-appointments",
            label: t("nav.schedule"),
            icon: Calendar,
            path: "/schedule",
            moduleKey: ModuleKey.APPOINTMENTS,
          },
          // {
          //   id: 'resource-scheduling',
          //   label: t('nav.resourceScheduling'),
          //   icon: Building2,
          //   path: '/resource-scheduling',
          //   moduleKey: ModuleKey.APPOINTMENTS
          // },
          {
            id: "provider-availability",
            label: t("nav.providerAvailability"),
            icon: Clock,
            path: "/provider-availability",
            // Gate on AVAILABILITY_READ specifically — APPOINTMENTS is
            // too coarse and lets Billing through, which then 403s on
            // /availability and /users when the page mounts.
            permission: Permission.AVAILABILITY_READ,
            moduleKey: ModuleKey.APPOINTMENTS,
          },
          // {
          //   id: 'waitlist-management',
          //   label: t('nav.waitlistManagement'),
          //   icon: ClipboardList,
          //   path: '/waitlist-management',
          //   moduleKey: ModuleKey.APPOINTMENTS
          // }
        ],
      },
      {
        // Single clinical group — everything a doctor reaches for mid-clinic
        // (prescribe, order, refer, scan) in one place. Previously split across
        // a separate "Orders" group and this one, which buried Pharmacy two
        // collapsibles deep. Pharmacy leads since it's the most-used.
        id: "clinical",
        label: t("nav.clinical"),
        icon: Stethoscope,
        children: [
          {
            // Pharmacy umbrella — single entry for Prescriptions, Refills,
            // Formulary, and Drug lookup. RECORD_READ covers Rx/refills/lookup;
            // MEDICATION_READ covers Formulary (also reachable by nurses).
            id: "pharmacy",
            label: isRTL ? "الصيدلية" : "Pharmacy",
            icon: Pill,
            path: "/pharmacy",
            permission: [
              Permission.MEDICAL_RECORDS_READ,
              Permission.MEDICATION_READ,
            ],
            badge: pendingRefills,
          },
          {
            id: "lab-orders",
            label: t("nav.labOrders"),
            icon: FlaskConical,
            path: "/lab-orders",
            moduleKey: ModuleKey.LAB_ORDERS,
          },
          {
            id: "imaging-orders",
            label: t("nav.imagingOrders"),
            icon: FileImage,
            path: "/imaging-orders",
            moduleKey: ModuleKey.IMAGING_ORDERS,
          },
          {
            // Clinic-wide referrals management. Self-scoped server-side: a doctor
            // sees their own referrals, owners/managers/front-desk see all.
            id: "referrals",
            label: isRTL ? "الإحالات" : "Referrals",
            icon: Share2,
            path: "/referrals",
            permission: Permission.REFERRAL_READ,
          },
          // "Scan document" moved out of the sidebar into the MaieLauncherPill
          // speed-dial (gated there by the same AI_OCR_INBOX_ENABLED flag).
        ],
      },
      {
        // Reusable clinical reference/config a clinic curates once and reuses:
        // note templates, care protocols, and the lab/imaging order catalog.
        id: "library",
        label: isRTL ? "المكتبة" : "Library",
        icon: BookOpen,
        children: [
          {
            id: "templates",
            label: t("nav.templates"),
            icon: ClipboardList,
            path: "/templates",
            moduleKey: ModuleKey.TEMPLATES,
          },
          {
            id: "protocols",
            label: isRTL ? "البروتوكولات" : "Protocols",
            icon: ClipboardList,
            path: "/protocols",
            moduleKey: ModuleKey.TEMPLATES,
          },
          // Order Catalog (lab + imaging) — re-enabled when the catalog page lands.
          {
            id: "order-catalog",
            label: isRTL ? "كتالوج الطلبات" : "Order Catalog",
            icon: FlaskConical,
            path: "/order-catalog",
            permission: [Permission.LAB_ORDER_READ, Permission.IMAGING_ORDER_READ],
          },
        ],
      },
      // {
      //   id: 'adt',
      //   label: t('nav.adt'),
      //   icon: Bed,
      //   path: '/adt',
      //   roles: [UserRole.DOCTOR, UserRole.NURSE, UserRole.ADMIN, UserRole.SUPER_ADMIN, UserRole.RECEPTIONIST],
      //   moduleKey: ModuleKey.ADT
      // },
      {
        // Everything that brings a patient to the clinic and everything they
        // send back. This group held five entries — Patient portal, Bots &
        // messaging, Messages, Leads queue, Opt-outs — which were two jobs
        // wearing five hats: operating conversations, and setting up the
        // channels they arrive on. Now it's exactly those two.
        // See docs/INBOX_CONSOLIDATION_PLAN.md.
        id: "inbound",
        label: isRTL ? "المرضى الواردون" : "Patients & channels",
        icon: Inbox,
        children: [
          {
            // Leads queue and Opt-outs used to sit here as their own entries.
            // They were projections of this same object — `bot_leads` is keyed
            // on (channel, remoteId), which IS the message thread key — so they
            // are now segments inside the Inbox rather than separate pages.
            id: "inbox",
            label: isRTL ? "صندوق الوارد" : "Inbox",
            icon: MessageSquare,
            path: "/inbox",
            // /communications/threads requires patient:read on the backend
            // (threads are per-patient). Without this gate Billing — which
            // has no patient permissions — sees the entry and then 403s.
            // The booking + mute segments are gated separately inside the page,
            // matching their own backend routes.
            permission: Permission.PATIENT_READ,
          },
          {
            // Patient portal and bots were separate pages (and the portal sat
            // under Administration next to Audit Logs, despite being the
            // clinic's free inbound channel). They're tabs of one setup surface
            // now. Mirrors BotsAdminController's hospital_settings:read.
            id: "channels",
            label: isRTL ? "القنوات" : "Channels",
            icon: Share2,
            path: "/channels",
            permission: Permission.HOSPITAL_SETTINGS_READ,
          },
        ],
      },
      {
        id: "billing",
        label: t("nav.billing"),
        icon: CreditCard,
        path: "/billing",
        moduleKey: ModuleKey.BILLING,
      },
      // {
      //   id: 'coding',
      //   label: t('coding.medicalCoding'),
      //   icon: FileText,
      //   path: '/coding',
      //   roles: [UserRole.BILLING_STAFF, UserRole.ADMIN, UserRole.SUPER_ADMIN],
      //   moduleKey: ModuleKey.BILLING
      // },
      // {
      //   id: 'reports',
      //   label: 'Reports',
      //   icon: BarChart3,
      //   path: '/reports',
      //   roles: [UserRole.DEPARTMENT_HEAD, UserRole.ADMIN, UserRole.SUPER_ADMIN, UserRole.DOCTOR]
      // },
      {
        id: "admin",
        label: t("common.administration"),
        icon: Shield,
        // The full admin section is OWNER-only on tenant deployments.
        // The permission check below is broad enough that any role with
        // user/system/audit read sees it, which under the MVP role catalog
        // is just OWNER (and IT_SUPPORT on self-hosted).
        permission: [Permission.USER_READ, Permission.SYSTEM_READ],
        children: [
          {
            id: "admin-members",
            label: t("nav.teamMembers"),
            icon: UserCog,
            path: "/admin/members",
            permission: Permission.USER_READ,
          },
          // Domains moved into /admin/system-settings as a tab — no
          // separate sidebar entry. The /admin/domains route still
          // resolves for old bookmarks but isn't surfaced here.
          {
            id: "admin-system-settings",
            label: t("admin.systemSettings"),
            icon: Server,
            path: "/admin/system-settings",
            permission: [
              Permission.SYSTEM_READ,
              Permission.HOSPITAL_SETTINGS_READ,
            ],
          },
          {
            id: "admin-audit-logs",
            label: t("admin.auditLogs"),
            icon: FileSearch,
            path: "/admin/audit-logs",
            permission: Permission.AUDIT_LOGS_READ,
          },
          {
            id: "admin-knowledge-base",
            label: isRTL ? "قاعدة المعرفة" : "Knowledge base",
            icon: BookOpen,
            path: "/admin/knowledge-base",
            permission: Permission.HOSPITAL_SETTINGS_READ,
          },
          {
            id: "admin-external-providers",
            label: isRTL ? "الجهات الخارجية" : "External providers",
            icon: Building2,
            path: "/admin/external-providers",
            permission: Permission.HOSPITAL_SETTINGS_READ,
          },
          // Feature toggles are no longer a tenant-facing surface — they
          // exist as a MAIE-internal A/B / per-tenant experiment knob and
          // belong under /platform-admin. Subscription-level gating runs
          // through the entitlements system instead. Route still resolves
          // for old bookmarks until the platform-admin counterpart lands.
          {
            id: "admin-subscription",
            label: t("nav.subscription"),
            icon: CreditCard,
            path: "/admin/subscription",
            // SaaS-side: this is MAIE billing the clinic for seats, NOT
            // the clinic billing patients. Restrict to Owner-class roles
            // — the clinic's billing/receptionist staff have
            // `billing:read` for patient invoices but should not see the
            // tenant's subscription with MAIE.
            permission: Permission.SYSTEM_READ,
          },
          // Legacy surfaces — routes still resolve so direct links don't
          // break, but they're hidden from the sidebar in favour of the
          // membership-flow Team members page above. Re-enable once the
          // create-user form is migrated to the invite flow.
          // {
          //   id: 'admin-users',
          //   label: t('userManagement.title'),
          //   icon: UsersIcon,
          //   path: '/admin/users',
          //   permission: Permission.USER_READ,
          // },
          // {
          //   id: 'admin-roles',
          //   label: t('roleManagement.title'),
          //   icon: Shield,
          //   path: '/admin/roles',
          //   permission: Permission.USER_READ,
          // },
        ],
      },
  ];
}

/** The nav diet: two labelled sections, one Clinic settings row, 11 elements. */
function buildDietNav(ctx: NavContext): SidebarItem[] {
  const { isRTL, t, pendingRefills } = ctx;

  return [
      {
        id: "home",
        label: isRTL ? "الرئيسية" : "Home",
        icon: Activity,
        path: "/home",
      },
      {
        // Chat-with-MAIE is a primary surface — orders, tasks, patient Q&A,
        // research — so it sits in the nav rather than only behind the
        // floating bubble's fullscreen button.
        id: "assistant",
        label: isRTL ? "اسأل MAIE" : "Ask MAIE",
        icon: Bot,
        path: "/assistant",
      },
      {
        // Sections are LABELS, not collapsibles. Half-open groups used to
        // hide whole features behind a chevron nobody clicked; now that the
        // nav is short there is nothing left worth collapsing.
        id: "sec-clinic",
        label: isRTL ? "العيادة" : "Clinic",
        icon: Stethoscope,
        children: [
          {
            id: "patients",
            label: t("nav.patients"),
            icon: Users,
            path: "/patients",
            moduleKey: ModuleKey.PATIENT_MANAGEMENT,
          },
          {
            // Provider availability used to be a sibling of Schedule. It is
            // an attribute OF the schedule, not a peer of it, so it lives as
            // a tab inside /schedule now.
            id: "schedule",
            label: t("nav.schedule"),
            icon: Calendar,
            path: "/schedule",
            moduleKey: ModuleKey.APPOINTMENTS,
          },
          {
            // A queue you sit in all day belongs at the top level, not inside
            // a group. /communications/threads needs patient:read.
            id: "inbox",
            label: isRTL ? "صندوق الوارد" : "Inbox",
            icon: Inbox,
            path: "/inbox",
            permission: Permission.PATIENT_READ,
            // A shared queue needs someone to share it with. Solo, every
            // message is already in the doctor's own hands.
            minShape: "practice",
          },
          {
            id: "billing",
            label: t("nav.billing"),
            icon: CreditCard,
            path: "/billing",
            moduleKey: ModuleKey.BILLING,
          },
        ],
      },
      {
        id: "sec-orders",
        label: isRTL ? "الطلب والصرف" : "Ordering",
        icon: FlaskConical,
        children: [
          {
            // Pharmacy umbrella — Prescriptions, Refills, Formulary, Drug
            // lookup. Most-used clinical surface, and it used to sit two
            // collapsibles deep.
            id: "pharmacy",
            label: isRTL ? "الصيدلية" : "Pharmacy",
            icon: Pill,
            path: "/pharmacy",
            permission: [
              Permission.MEDICAL_RECORDS_READ,
              Permission.MEDICATION_READ,
            ],
            badge: pendingRefills,
          },
          {
            // Labs, imaging and referrals were three rows for one job: things
            // ordered and not yet back. One queue, one badge, segments inside
            // (/orders?type=). Gated on ANY of the three — a receptionist with
            // only lab_order:read still gets the row, and the page opens on
            // the segment she can actually see.
            id: "orders",
            label: isRTL ? "الطلبات" : "Orders",
            icon: FlaskConical,
            path: "/orders",
            permission: [
              Permission.LAB_ORDER_READ,
              Permission.IMAGING_ORDER_READ,
              Permission.REFERRAL_READ,
            ],
          },
        ],
      },
      {
        // One row for everything a clinic configures. Replaces the six-entry
        // Administration group AND the Library group (note templates,
        // protocols, order catalog) plus Channels — all curate-once
        // surfaces, which is the definition of configuration.
        //
        // Gated exactly as the old Administration group was: user:read
        // (Owner, Clinic Manager) or system:read (Owner, plus IT_SUPPORT on
        // self-hosted).
        id: "clinic",
        label: isRTL ? "إعدادات العيادة" : "Clinic settings",
        icon: Building2,
        path: "/clinic",
        permission: [Permission.USER_READ, Permission.SYSTEM_READ],
      },
  ];
}

/**
 * ModuleKey → the permissions that module needs. Mirrors `FeatureGuard`'s
 * map; a module the user cannot read is hidden rather than shown and 403'd.
 */
export const MODULE_PERMISSIONS: Record<ModuleKey, Permission[]> = {
  [ModuleKey.PATIENT_MANAGEMENT]: [Permission.PATIENT_READ],
  [ModuleKey.APPOINTMENTS]: [Permission.APPOINTMENT_READ],
  [ModuleKey.MEDICAL_RECORDS]: [Permission.MEDICAL_RECORDS_READ],
  [ModuleKey.LAB_ORDERS]: [Permission.LAB_ORDER_READ],
  [ModuleKey.IMAGING_ORDERS]: [Permission.IMAGING_ORDER_READ],
  [ModuleKey.BILLING]: [Permission.BILLING_READ],
  [ModuleKey.TEMPLATES]: [Permission.TEMPLATE_READ],
  [ModuleKey.CLINICAL_DECISION_SUPPORT]: [Permission.MEDICAL_RECORDS_READ],
  [ModuleKey.VOICE_AI]: [Permission.MEDICAL_RECORDS_READ],
};

/** What the visibility predicate needs. Deliberately not the auth store. */
export interface NavAccess {
  /** False before auth resolves — module permission checks are skipped then. */
  hasUser: boolean;
  /**
   * Derived clinic size. Defaults to the widest shape so a nav never starts
   * small and grows rows under the cursor while /me/clinic-shape is in flight.
   */
  shape?: ClinicShape;
  /** Owner escape hatch — show every row regardless of size. */
  showEverything?: boolean;
  hasPermission: (permission: Permission) => boolean;
  hasAnyPermission: (permissions: Permission[]) => boolean;
  hasRole: (roles: UserRole[]) => boolean;
  isModuleEnabled: (moduleKey: ModuleKey) => boolean;
}

/**
 * Whether one item is visible to this user. Behaviour is unchanged from the
 * copy that used to live in `Sidebar`; it is only reachable from tests now.
 */
export function isItemVisible(item: SidebarItem, access: NavAccess): boolean {
  // Size gate first, and it only ever subtracts. `showEverything` is the one
  // switch a clinic that does not fit the pattern can flip in Clinic settings.
  if (item.minShape && !access.showEverything) {
    if (!shapeAtLeast(access.shape ?? "group", item.minShape)) return false;
  }

  // A module switched off for the clinic hides its entries outright.
  if (item.moduleKey && !access.isModuleEnabled(item.moduleKey)) {
    return false;
  }

  // Permissions come from the live `/auth/me` payload rather than a static
  // role→permission map — that map was hand-maintained and drifted from the
  // backend's ROLE_DEFINITIONS, surfacing entries the user then 403'd on.
  if (item.moduleKey && access.hasUser) {
    const required = MODULE_PERMISSIONS[item.moduleKey];
    if (required && !access.hasAnyPermission(required)) {
      return false;
    }
  }

  // Explicit permissions, for items without a moduleKey (admin entries).
  // An array means ANY — so every member of it genuinely grants the item.
  if (item.permission) {
    return Array.isArray(item.permission)
      ? access.hasAnyPermission(item.permission)
      : access.hasPermission(item.permission);
  }

  // Legacy: explicit roles. Kept for backward compatibility.
  if (item.roles) {
    return access.hasRole(item.roles);
  }

  // A group with no path of its own is visible iff a child is. Otherwise its
  // eyebrow row would sit above an empty list, and clicking through would 403.
  if (item.children && item.children.length > 0 && !item.path) {
    return item.children.some((child) => isItemVisible(child, access));
  }

  return true;
}

/**
 * The nav this user actually gets: prune invisible items, and invisible
 * children out of the groups that survive. Pure — hand it a fake `NavAccess`
 * and it will tell you what a Nurse sees without rendering anything.
 */
export function filterNav(
  items: SidebarItem[],
  access: NavAccess,
): SidebarItem[] {
  return items
    .filter((item) => isItemVisible(item, access))
    .map((item) =>
      item.children
        ? { ...item, children: filterNav(item.children, access) }
        : item,
    );
}
